import React, { useEffect, useState } from 'react';
import '../../styled/pages/dashboard.scss';
import {
  Header,
  SideMenu,
  ReportTemplatePreview,
  Loader,
  Success,
  Failed,
} from '../../components';
import { customStyles } from '../../Utils/utils';
import { useNavigate, useLocation } from 'react-router-dom';
import { getHospitalReportsList } from '../../rtk-apis/reports/getHopsitalReportsList';
import { AppDispatch, RootState } from '../../store/store';
import { useDispatch, useSelector } from 'react-redux';
import { changeReportStatus } from '../../rtk-apis/reports/changeReportStatus';
import { clearChangeReportStatus } from '../../store/slices/reports/changeReportStatus';
import CryptoJS from 'crypto-js';
import { getHospitalId, getRole, getUserId } from '../../Utils/authUtils';
import { deleteReport } from '../../rtk-apis/reports/deleteReport';
import { clearDeleteReport } from '../../store/slices/reports/deleteReport';
import { canDeleteReport, isDoctorRole, isSonographerOrTechnicianRole, isAssignedDoctor, canUnfinalizeReport } from '../../Utils/reportPermissions';
import ConfirmationModal from '../../components/ConfirmationModal';
import { sendReminder } from '../../rtk-apis/reports/sendReminder';
import { sendReportNotification } from '../../rtk-apis/reports/sendReportNotification';
import { getDistributionCountsApi, DistributionInfo } from '../../rtk-apis/hl7/getDistributionCounts';
import { getPatientNameOverridesApi } from '../../rtk-apis/reports/getPatientNameOverrides';
import CardiacReportList from './CardiacReportList';
import { useClinicFeatures } from '../../Utils/useClinicFeatures';

function useQuery() {
  const { search } = useLocation();
  return React.useMemo(() => new URLSearchParams(search), [search]);
}

const ReportList = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const query = useQuery();
  // Per-clinic feature control: the Measurement row action only exists for a
  // clinic that has the measurement module (Super Admin → Feature control).
  const { measurementEnabled } = useClinicFeatures();
  const { status, data, loading } = useSelector(
    (state: RootState) => state.getHospitalReportsListResponse
  );

  const [tableData, setTableData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [itemsPerPage, setItemsPerPage] = useState(15);

  // Pagination state from API response
  const [paginationData, setPaginationData] = useState({
    currentPage: 1,
    pageSize: 30,
    totalRecords: 0,
    totalPages: 0,
  });

  const [selectReportTemplateModal, setSelectReportTemplateModal] = useState(false);
  const [onAssigningReport, setOnAssigningReport] = useState(false);
  const hospitalid = getHospitalId();

  const [ReportTypeFilterValue, setReportTypeFilterValue] = useState('');
  const [ReportStatusFilterValue, setReportStatusFilterValue] = useState('');
  const [EndDateFilerValue, setEndDateFilerValue] = useState('');
  const [StartDateFilerValue, setStartDateFilerValue] = useState('');
  const [ReportAssignedToDoc, setReportAssignedToDoc] = useState('');
  const [FilterApplied, setFilterApplied] = useState(false);
  const [ClearFilter, setClearFilter] = useState(false);
  const [currentFilters, setCurrentFilters] = useState({
    reportAssignedTo: query.get('assignedTo') || '',
    startDate: query.get('startDate') || '',
    endDate: query.get('endDate') || '',
    reportStatus: query.get('status') || '',
    reportType: query.get('type') || '',
    labels: query.get('labels') || '',
    measurementFilters: query.get('measures') || '',
  });
  const [searchTerm, setSearchTerm] = useState(query.get('q') || '');
  const [currentPage, setCurrentPage] = useState(Number(query.get('page')) || 1);

  const [displayMessage, setDisplayMessage] = useState('');
  const [isSuccessModalOpen, setIsSuccessModalOpen] = useState(false);
  const [isFailedModalOpen, setIsFailedModalOpen] = useState(false);

  useEffect(() => {
    const params = new URLSearchParams({
      page: String(currentPage),
      q: searchTerm || '',
      assignedTo: currentFilters.reportAssignedTo || '',
      startDate: currentFilters.startDate || '',
      endDate: currentFilters.endDate || '',
      status: currentFilters.reportStatus || '',
      labels: currentFilters.labels || '',
      type: currentFilters.reportType || '',
      measures: currentFilters.measurementFilters || '',
    });

    [...params.keys()].forEach(k => {
      if (!params.get(k)) params.delete(k);
    });

    navigate({ pathname: location.pathname, search: params.toString() }, { replace: true });
  }, [currentPage, searchTerm, currentFilters, navigate, location.pathname]);

  const {
    status: changeReportStatusStatus,
    data: changeReportStatusData,
    msg,
    loading: changeReportStatusLoading,
  } = useSelector((state: RootState) => state.changeReportStatusResponse);

  const {
    status: deleteReportStatus,
    msg: deleteReportMsg,
  } = useSelector((state: RootState) => state.deleteReportResponse);

  const userRole = getRole();
  const loggedInUserId = getUserId();
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [reportToDelete, setReportToDelete] = useState<any>(null);
  const [sendingReminder, setSendingReminder] = useState(false);
  const [distributionCounts, setDistributionCounts] = useState<Record<string, DistributionInfo>>({});
  const [nameOverrides, setNameOverrides] = useState<Record<string, string>>({});
  const [notifTargetItem, setNotifTargetItem] = useState<any>(null);
  const [notifMessage, setNotifMessage] = useState('');
  const [isSendingNotif, setIsSendingNotif] = useState(false);
  const [notifResult, setNotifResult] = useState<{ msg: string; ok: boolean } | null>(null);

  const finalizeReport = (reportId, reportStatus) => {
    const payload = {
      reportId,
      reportStatus,
    };
    dispatch(changeReportStatus(payload));
  };

  const handleSendReminder = async (item: any) => {
    if (sendingReminder) return;
    setSendingReminder(true);
    try {
      const result = await dispatch(sendReminder({ reportId: item._id })).unwrap();
      if (result.status === 'SUCCESS') {
        setDisplayMessage(`Doctor reminded for "${item.reportName || item.name}"`);
        setIsSuccessModalOpen(true);
        setTimeout(() => {
          setIsSuccessModalOpen(false);
          setDisplayMessage('');
          getReportsList();
        }, 2000);
      }
    } catch (err: any) {
      setDisplayMessage(err || 'Failed to send reminder');
      setIsFailedModalOpen(true);
      setTimeout(() => {
        setIsFailedModalOpen(false);
        setDisplayMessage('');
      }, 2000);
    } finally {
      setSendingReminder(false);
    }
  };

  useEffect(() => {
    const handleSuccess = message => {
      setDisplayMessage(message);
      setIsSuccessModalOpen(true);
      setTimeout(() => {
        setIsSuccessModalOpen(false);
        setDisplayMessage('');
        getReportsList();
      }, 1500);
    };

    const handleFailure = message => {
      setDisplayMessage(message);
      setIsFailedModalOpen(true);
      setTimeout(() => {
        setIsFailedModalOpen(false);
        setDisplayMessage('');
      }, 1500);
    };
    if (changeReportStatusStatus === 'SUCCESS') {
      console.log('SUCCESS');
      handleSuccess(msg);
    }

    if (changeReportStatusStatus === 'FAILED') {
      handleFailure(msg);
    }

    return () => {
      dispatch(clearChangeReportStatus());
    };
  }, [changeReportStatusStatus]);

  useEffect(() => {
    if (deleteReportStatus === 'SUCCESS') {
      setDisplayMessage(deleteReportMsg || 'Report deleted successfully');
      setIsSuccessModalOpen(true);
      setTimeout(() => {
        setIsSuccessModalOpen(false);
        setDisplayMessage('');
        getReportsList();
      }, 1500);
    }
    if (deleteReportStatus === 'FAILED') {
      setDisplayMessage(deleteReportMsg || 'Failed to delete report');
      setIsFailedModalOpen(true);
      setTimeout(() => {
        setIsFailedModalOpen(false);
        setDisplayMessage('');
      }, 1500);
    }
    return () => {
      dispatch(clearDeleteReport());
    };
  }, [deleteReportStatus]);

  const handleDeleteReport = (item: any) => {
    setReportToDelete(item);
    setDeleteConfirmOpen(true);
  };

  const confirmDeleteReport = () => {
    if (reportToDelete?._id) {
      dispatch(deleteReport({ reportId: reportToDelete._id }));
    }
    setDeleteConfirmOpen(false);
    setReportToDelete(null);
  };

  const actions = [
    {
      label: 'more',
      iconName: 'more-icon',
      dropdownItems: [
        {
          id: 'Draft',
          label: 'Draft',
          iconName: 'report-assign',
          isVisible: item => {
            if (item.status === 'Draft') return false;
            if (isSonographerOrTechnicianRole(userRole)) return item.status !== 'Finalized';
            if (isDoctorRole(userRole)) {
              if (item.status === 'Finalized') return canUnfinalizeReport(userRole, item.status, item.finalizedById, loggedInUserId);
              return isAssignedDoctor(userRole, item.reportAssignedToId, loggedInUserId);
            }
            return false;
          },
          onClick: item => {
            finalizeReport(item._id, 'Draft');
          },
        },
        {
          id: 'Preliminary',
          label: 'Preliminary',
          iconName: 'report-assign',
          isVisible: item => {
            if (item.status === 'Preliminary') return false;
            if (isSonographerOrTechnicianRole(userRole)) return item.status !== 'Finalized';
            if (isDoctorRole(userRole)) {
              if (item.status === 'Finalized') return canUnfinalizeReport(userRole, item.status, item.finalizedById, loggedInUserId);
              return isAssignedDoctor(userRole, item.reportAssignedToId, loggedInUserId);
            }
            return false;
          },
          onClick: item => {
            finalizeReport(item._id, 'Preliminary');
          },
        },
        {
          id: 'Finalized',
          label: 'Finalized',
          iconName: 'report-assign',
          isVisible: item => {
            if (item.status === 'Finalized') return false;
            if (!isDoctorRole(userRole)) return false;
            return isAssignedDoctor(userRole, item.reportAssignedToId, loggedInUserId);
          },
          onClick: item => {
            finalizeReport(item._id, 'Finalized');
          },
        },
        {
          id: 'measurement',
          label: 'Measurement',
          iconName: 'report-assign',
          // Receptionists must never reach the measurement workspace (reports only).
          isVisible: item =>
            measurementEnabled && userRole !== 'Medical Receptionist' && !!item?.studyInstanceUID,
          onClick: item => {
            if (item?.studyInstanceUID) {
              navigate(`/measurement?StudyInstanceUIDs=${item.studyInstanceUID}`);
            }
          },
        },
        {
          id: 'send-reminder',
          label: '🔔 Remind Doctor',
          iconName: 'report-assign',
          isVisible: item =>
            userRole === 'Medical Receptionist' &&
            item.status !== 'Finalized' &&
            !item.pendingReview,
          onClick: item => {
            handleSendReminder(item);
          },
        },
        {
          id: 'view-report',
          label: 'View Report',
          iconName: 'report-preview',
          onClick: item => {
            navigate(`/patientReports/${item?._id}`, {
              state: {
                reportId: item?._id,
              },
            });
          },
        },
        {
          id: 'delete-report',
          label: 'Delete Report',
          iconName: 'report-assign',
          isVisible: item => canDeleteReport(userRole, item?.status, item?.reportAssignedToId, loggedInUserId),
          onClick: item => {
            handleDeleteReport(item);
          },
        },
        {
          id: 'send-notification',
          label: 'Send Notification',
          iconName: 'report-assign',
          isVisible: item => !!item?._id && !!item?.reportAssignedToId,
          onClick: item => {
            setNotifTargetItem(item);
            setNotifMessage('');
            setNotifResult(null);
          },
        },
        {
          id: 'distribute',
          label: 'Distribute',
          iconName: 'report-assign',
          onClick: item => {
            if (item?.studyInstanceUID) {
              navigate(`/distribution/${item.studyInstanceUID}`, {
                state: {
                  reportId: item?._id,
                  studyData: {
                    name: item?.name,
                    patientName: item?.name,
                    dob: item?.dob,
                    sex: item?.sex,
                    age: item?.age,
                    patientId: item?.patientId,
                    reportName: item?.reportName,
                    date: item?.date,
                    time: item?.time,
                    studyDate: item?.studyDate,
                    studyTime: item?.studyTime,
                    modality: item?.modality,
                    reportAssignedTo: item?.assignTo ?? item?.reportAssignedTo,
                    status: item?.status,
                  },
                },
              });
            }
          },
        },
      ],
      onClick: () => {
        setSelectReportTemplateModal(true);
      },
    },
  ];

  useEffect(() => {
    getReportsList();
  }, [currentPage, searchTerm, currentFilters]);

  useEffect(() => {
    if (onAssigningReport) {
      getReportsList();
      setOnAssigningReport(false);
    }
  }, [onAssigningReport]);

  const AES_SECRET_KEY = process.env.NEXT_PUBLIC_AES_SECRET_KEY;
  const AES_IV = process.env.NEXT_PUBLIC_AES_IV;

  const decryptData = encryptedData => {
    try {
      if (!AES_SECRET_KEY || !AES_IV) {
        console.error('Missing encryption key or IV');
        return null;
      }

      const key = CryptoJS.enc.Hex.parse(AES_SECRET_KEY);
      const iv = CryptoJS.enc.Hex.parse(AES_IV);

      const cipherParams = CryptoJS.enc.Hex.parse(encryptedData);

      const decrypted = CryptoJS.AES.decrypt({ ciphertext: cipherParams }, key, {
        iv: iv,
        mode: CryptoJS.mode.CBC,
        padding: CryptoJS.pad.Pkcs7,
      });

      const decryptedText = decrypted.toString(CryptoJS.enc.Utf8);

      if (!decryptedText) {
        console.error('Decryption failed! Possible wrong format.');
        return null;
      }

      return decryptedText;
    } catch (error) {
      console.error('Decryption error:', error);
      return null;
    }
  };

  const getReportsList = async () => {
    if (hospitalid) {
      const payload = {
        page: currentPage,
        limit: itemsPerPage,
        search: searchTerm,
        hospitalId: hospitalid,
        reportAssignedTo: currentFilters.reportAssignedTo,
        startDate: currentFilters.startDate,
        endDate: currentFilters.endDate,
        reportStatus: currentFilters.reportStatus,
        reportType: currentFilters.reportType,
        labels: currentFilters.labels,
        measurementFilters: currentFilters.measurementFilters,
      };

      try {
        const response = await dispatch(getHospitalReportsList(payload));

        if (response?.payload?.encryptedData) {
          const decryptedData = decryptData(response?.payload?.encryptedData);

          if (decryptedData) {
            try {
              const parsedData = JSON.parse(decryptedData);

              // Set the table data from the API response
              if (parsedData.data && Array.isArray(parsedData.data)) {
                setTableData(parsedData.data);
                setFilteredData(parsedData.data);
              } else {
                setTableData([]);
                setFilteredData([]);
              }

              // Set pagination data from the API response
              if (parsedData.pagination) {
                setPaginationData({
                  currentPage: parsedData.pagination.currentPage || 1,
                  pageSize: parsedData.pagination.pageSize || 30,
                  totalRecords: parsedData.pagination.totalRecords || 0,
                  totalPages: parsedData.pagination.totalPages || 0,
                });

                // Update itemsPerPage if it's different from API
                if (parsedData.pagination.pageSize !== itemsPerPage) {
                  setItemsPerPage(parsedData.pagination.pageSize);
                }
              }
            } catch (parseError) {
              console.error('JSON Parse Error:', parseError);
              setTableData([]);
              setFilteredData([]);
              setPaginationData({
                currentPage: 1,
                pageSize: 30,
                totalRecords: 0,
                totalPages: 0,
              });
            }
          } else {
            setTableData([]);
            setFilteredData([]);
            setPaginationData({
              currentPage: 1,
              pageSize: 30,
              totalRecords: 0,
              totalPages: 0,
            });
            console.error('Failed to decrypt response data.');
          }
        } else {
          console.error('API response does not contain encryptedData.');
          setTableData([]);
          setFilteredData([]);
          setPaginationData({
            currentPage: 1,
            pageSize: 30,
            totalRecords: 0,
            totalPages: 0,
          });
        }
      } catch (error) {
        console.error('Error fetching reports:', error);
        setTableData([]);
        setFilteredData([]);
        setPaginationData({
          currentPage: 1,
          pageSize: 30,
          totalRecords: 0,
          totalPages: 0,
        });
      }

      setFilterApplied(false);
    }
  };

  // Handle page change
  const handlePageChange = newPage => {
    setCurrentPage(newPage);
    // The useEffect will trigger getReportsList when currentPage changes
  };

  // Handle items per page change
  const handleItemsPerPageChange = newItemsPerPage => {
    setItemsPerPage(newItemsPerPage);
    setCurrentPage(1); // Reset to first page when changing items per page
  };

  const handleFilterChange = filters => {
    setCurrentFilters(filters);
    setCurrentPage(1); // Reset to first page when applying filters
  };

  const handleClearFilter = () => {
    setCurrentFilters({
      reportAssignedTo: '',
      startDate: '',
      endDate: '',
      reportStatus: '',
      reportType: '',
      labels: '',
      measurementFilters: '',
    });
    setCurrentPage(1); // Reset to first page when clearing filters
  };

  // For search, we'll use local filtering since it's debounced
  useEffect(() => {
    if (searchTerm) {
      const filtered = tableData.filter(item =>
        item?.name?.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredData(filtered);
    } else {
      setFilteredData(tableData);
    }
  }, [searchTerm, tableData]);

  useEffect(() => {
    if (!tableData.length) {
      setDistributionCounts({});
      return;
    }
    const uids = tableData.map((r: any) => r.studyInstanceUID).filter(Boolean);
    if (!uids.length) return;
    dispatch(getDistributionCountsApi({ studyInstanceUIDs: uids }))
      .then((action: any) => {
        if (getDistributionCountsApi.fulfilled.match(action)) {
          setDistributionCounts(action.payload || {});
        }
      })
      .catch(() => setDistributionCounts({}));
  }, [tableData]);

  useEffect(() => {
    if (!tableData.length || !hospitalid) {
      setNameOverrides({});
      return;
    }
    const uids = tableData.map((r: any) => r.studyInstanceUID).filter(Boolean);
    if (!uids.length) return;
    dispatch(getPatientNameOverridesApi({ studyInstanceUIDs: uids, hospitalId: hospitalid }))
      .then((action: any) => {
        if (getPatientNameOverridesApi.fulfilled.match(action)) {
          setNameOverrides(action.payload?.result || {});
        }
      })
      .catch(() => setNameOverrides({}));
  }, [tableData, hospitalid]);

  // Refetch report list when the tab becomes visible again (e.g. after viewing a report)
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        getReportsList();
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [currentPage, searchTerm, currentFilters]);

  return (
    <>
      <section
        className="layoutContainer cw-listpage"
        style={customStyles.layoutBackground}
      >
        <div className="layoutGrid">
          {/* All roles (incl. Medical Receptionist) use the new CardiacReportList.
              The old table's receptionist conditions live on: clickable distribution
              label + reminder bell ported into CardiacReportList; status/action
              gating already enforced via the dropdownItems isVisible callbacks. */}
          <div className="page page--fullBody">
              <Header />
              <div className="page__body">
                <CardiacReportList
                  reports={(searchTerm ? filteredData : tableData).map((r: any) => ({
                    ...r,
                    _distData: distributionCounts[r.studyInstanceUID] ?? null,
                    name: nameOverrides[r.studyInstanceUID] || r.name,
                  }))}
                  actions={actions}
                  searchTerm={searchTerm}
                  setSearchTerm={setSearchTerm}
                  currentFilters={currentFilters}
                  onFilterChange={handleFilterChange}
                  onClearFilter={handleClearFilter}
                  pagination={paginationData}
                  onPageChange={handlePageChange}
                  onItemsPerPageChange={handleItemsPerPageChange}
                  loading={loading}
                  role={userRole}
                  onAssigned={() => setOnAssigningReport(true)}
                />
              </div>
            </div>
        </div>
      </section>
      <ReportTemplatePreview
        isOpen={selectReportTemplateModal}
        closeModal={() => {
          setSelectReportTemplateModal(false);
        }}
      />
      <Success
        isSuccessModalOpen={isSuccessModalOpen}
        title={displayMessage}
        handleCloseModal={() => setIsSuccessModalOpen(false)}
      />
      <Failed
        isFailedModalOpen={isFailedModalOpen}
        title={displayMessage}
        handleCloseModal={() => setIsFailedModalOpen(false)}
      />
      <ConfirmationModal
        isConfirmationModalOpen={deleteConfirmOpen}
        title="Delete Report"
        description={`Are you sure you want to delete the report for "${reportToDelete?.name || ''}"? This action cannot be undone.`}
        confirmActionText="Yes, Delete"
        cancelActionText="Cancel"
        handleConfirm={confirmDeleteReport}
        handleCloseModal={() => {
          setDeleteConfirmOpen(false);
          setReportToDelete(null);
        }}
        isPatientReport={false}
      />

      {notifTargetItem && (
        <div
          style={{
            position: 'fixed', inset: 0, zIndex: 9999,
            background: 'rgba(0,0,0,0.55)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}
          onClick={() => setNotifTargetItem(null)}
        >
          <div
            style={{
              background: '#1a2336', borderRadius: 10, padding: '28px 28px 24px',
              width: 420, boxShadow: '0 8px 32px rgba(0,0,0,0.4)',
            }}
            onClick={e => e.stopPropagation()}
          >
            <h3 style={{ margin: '0 0 4px', fontSize: 16, color: '#e2e8f0' }}>Send Notification</h3>
            <p style={{ margin: '0 0 16px', fontSize: 13, color: '#94a3b8' }}>{notifTargetItem.name}</p>
            <textarea
              rows={4}
              placeholder="Type your message..."
              value={notifMessage}
              onChange={e => setNotifMessage(e.target.value)}
              style={{
                width: '100%', boxSizing: 'border-box',
                background: '#0f1824', border: '1px solid #2d3f5a',
                borderRadius: 6, color: '#e2e8f0', padding: '10px 12px',
                fontSize: 14, resize: 'vertical',
              }}
            />
            {notifResult && (
              <p style={{ marginTop: 8, fontSize: 13, color: notifResult.ok ? '#4ade80' : '#f87171' }}>
                {notifResult.msg}
              </p>
            )}
            <div style={{ display: 'flex', gap: 10, marginTop: 16, justifyContent: 'flex-end' }}>
              <button type="button" className="btn btn__secondary" onClick={() => setNotifTargetItem(null)}>
                Cancel
              </button>
              <button
                type="button"
                className="btn btn__primary"
                disabled={!notifMessage.trim() || isSendingNotif}
                onClick={async () => {
                  if (!notifTargetItem?._id || !notifMessage.trim()) return;
                  setIsSendingNotif(true);
                  setNotifResult(null);
                  const result = await dispatch(sendReportNotification({ reportId: notifTargetItem._id, message: notifMessage.trim() }));
                  setIsSendingNotif(false);
                  if (sendReportNotification.fulfilled.match(result)) {
                    setNotifMessage('');
                    setNotifResult({ msg: 'Notification sent.', ok: true });
                    setTimeout(() => setNotifTargetItem(null), 1200);
                  } else {
                    setNotifResult({ msg: (result.payload as string) || 'Failed to send.', ok: false });
                  }
                }}
              >
                {isSendingNotif ? 'Sending...' : 'Send'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ReportList;
